import { InputText } from 'component-library/index';
import React, { useEffect, useRef, useState, memo } from 'react';
import { useField } from '../../../providers/ModernAcquisitionProvider/useField';
import { TErrorType } from '../../../providers/ModernAcquisitionProvider/types';
import SlideDown from '../../SlideDown/SlideDown';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import { isValidEmailFormat, wrapTrademarkSuperscript } from '../../../utils/cob-utils';
import EmailAutoCompleteListBox from './sub-components/EmailAutoCompleteListBox/EmailAutoCompleteListBox';
import { generateEmailFormatErrorMessage, generateFieldErrorMessage } from '../../../utils/error-message';
import useBriteVerify from '../../../hooks/useBriteVerify';
import SessionStorage from '../../../utils/SessionStorage';

export interface IEmailFieldWithProvider {
    fieldKey: 'emailAddress';
    name: 'Email Address';
    label?: string;
    id?: string;
    disabled?: boolean;
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
    emailAutofill?: Array<string>;
    emailMessage?: string;
}

const MemoizedInputText = memo(InputText);

export const EmailFieldWithProvider = memo(function EmailFieldWithProvider(props: IEmailFieldWithProvider & React.HTMLAttributes<HTMLInputElement>, ...rest) {

    const { field, updateField } = useField(props.fieldKey);
    const ref = useRef<HTMLInputElement>(null); 
    const [autofillIndex, setAutofillIndex] = useState<number>();
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [isSuggestionsOpen, setIsSuggestionsOpen] = useState<boolean>(false);
    const { emailValidationApi } = useBriteVerify();
    const validationVersion = useRef<number>(0);

    useEffect(() => (): void => {
        ++validationVersion.current;
    }, []);

    const handleFocus = (): void => {
        ++validationVersion.current;
        setIsFocused(true);
        if (field.state === 'success') {
            updateField({ ...field, state: 'neutral' });
        }
        props.onFocusCallback && props.onFocusCallback();
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        ++validationVersion.current;
        const formatted = parseValue(event.target.value);
        updateField({ ...field, value: formatted });
        if (props.emailAutofill && formatted.includes('@')) {
            setIsSuggestionsOpen(true);
        } else {
            setIsSuggestionsOpen(false);
        }
    }

    const validateEmailWithBrite = async (email: string): Promise<boolean> => {
        const isAdobeMode = SessionStorage.contains('adobeMode');
        const useEmailApi = SessionStorage.contains('useEmailApi');
        const isAuthor = ['4502', '3000'].some((entry) => window.location.port.includes(entry));
        if (isAuthor || (isAdobeMode && !useEmailApi)) {
            return true;
        }
        return !!(await emailValidationApi(email));
    }

    const handleBlur = async (): Promise<void> => {
        const version = ++validationVersion.current;
        const trimmedValue = parseValue(field.value?.trim() ?? '');
        updateField({ ...field, value: trimmedValue, state: 'neutral' });
        const error: TErrorType = trimmedValue.length === 0
            ? 'requiredField'
            : !isValidEmailFormat(trimmedValue)
                ? 'invalidInput'
                : !(await validateEmailWithBrite(trimmedValue))
                    ? 'thirdParty'
                    : 'None';

        if (version !== validationVersion.current) return;
        setIsFocused(false);
        if (error === 'None') {
            updateField({ ...field, value: trimmedValue, state: 'success', errorType: 'None' })
        } else {
            updateField({ ...field, value: trimmedValue, state: 'error', errorType: error })
        }
        props.onBlurCallback && props.onBlurCallback();
    }

    useEffect(() => {
        if (
            filteredEmailsSuffixes &&
            filteredEmailsSuffixes.length > 0 &&
            isSuggestionsOpen &&
            ref.current
        ) {
            ref.current.addEventListener('keydown', handleKeydown);
            return () => ref.current?.removeEventListener('keydown', handleKeydown);
        }
    }),[isSuggestionsOpen];

    const filteredEmailsSuffixes: string[] = field.value
        ? props.emailAutofill?.filter((suffix: string): boolean =>
            !!suffix && suffix.includes(field.value.slice(field.value.indexOf('@')))) ?? []
        : [];

    const parseAndSetValue = (value: string): void => {
        ++validationVersion.current;
        updateField({ ...field, value: parseValue(value) });
    }

    const parseValue = (value: string): string => {
        if (!value) {
            return '';
        }
        const spaceRegex = new RegExp(/[\s,=]/g);
        const periodRegex = new RegExp(/\.\./g);
        const hasMultipleAtRegex = new RegExp(/@(.*)@/);
        const LOCAL_ADDRESS_MAX = 64;

        while (spaceRegex.test(value) || periodRegex.test(value)) {
            value = value
                .replace(spaceRegex, '')
                .replace(periodRegex, '.').trim();
        }

        const hasAt = value.includes('@');
        while (value.length > 0 && value[0] === '.') {
            value = value.replace('.', '');
        }

        if (!hasAt && value.length > LOCAL_ADDRESS_MAX) {
            value = value.substring(0, LOCAL_ADDRESS_MAX);
        } else if (hasAt) {
            if (hasMultipleAtRegex.test(value)) {
                const lastOccurrence = value.lastIndexOf('@');
                value = value.slice(0, lastOccurrence) + value.slice(lastOccurrence + 1);
            }
        }
        return value;
    };

    const handleKeydown = (e: KeyboardEvent) => {
        switch (e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setAutofillIndex(current => {
                    if (current === undefined || current === 0)
                        return filteredEmailsSuffixes!.length - 1;
                    return current - 1;
                });
                break;
            case 'ArrowDown':
                e.preventDefault();
                setAutofillIndex(current => {
                    if (
                        current === undefined ||
                        current === filteredEmailsSuffixes!.length - 1
                    ) {
                        return 0;
                    }
                    return current + 1;
                });
                break;
            case 'Enter':
            case 'Tab':
                const target = e.target as HTMLInputElement;
                if (!isValidEmailFormat(target.value)) {
                    e.preventDefault();
                    if (typeof autofillIndex === 'number') {
                        parseAndSetValue(field.value.slice(0, field.value.indexOf('@')) + filteredEmailsSuffixes![autofillIndex]);
                        setIsSuggestionsOpen(false);
                    }
                }
                break;
        }
    };

    return (
        <div className='EmailField relative'>
            <MemoizedInputText
                required
                autoComplete='off'
                ref={ref}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === ' ' && e.preventDefault()}
                name={props.name}
                label={props.label || props.name}
                id={props.id}
                value={field.value}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
                errorMessage={field.errorType === 'requiredField'
                    ? `${generateFieldErrorMessage(props.name, 'missingValue')}: email@domain.com`
                    : field.errorType === 'thirdParty'
                        ? generateFieldErrorMessage(props.name, 'thirdParty', 'Email')
                        : generateEmailFormatErrorMessage(props.name)}
                state={field.state}
                maxLength={255}
                disabled={props.disabled}
                {...rest}
            />
            {props.emailMessage && isFocused && (
                <SlideDown open={isFocused} className='EmailField__note'>
                    {transformHTMLParserLinks(wrapTrademarkSuperscript(props.emailMessage))}
                </SlideDown>
            )}
            {isFocused && isSuggestionsOpen && props.emailAutofill && (
                <EmailAutoCompleteListBox
                    className='placedTopProvider'
                    filteredEmailsSuffixes={filteredEmailsSuffixes}
                    currentIndex={autofillIndex}
                    setCurrentIndex={setAutofillIndex}
                    setValue={parseAndSetValue}
                    currentValue={field.value}
                />
            )}
        </div>
    );
});

export default EmailFieldWithProvider;
